// Canonical step IDs for WLC referral funnel analytics.
//
// Every screen the user can see — welcome carousel slides, questions, break /
// below-threshold / consent virtual screens — has a stable stepId. These IDs
// are written to Firestore via logReferralFormEvent and must stay in sync with
// ALLOWED_STEP_IDS in python-functions/wlc_functions.py.
//
// Why a dedicated module (not just ALL_QUESTIONS)?
// - Virtual screens (welcome, break, consent) are not in the question array.
// - The backend allowlist needs an explicit, enumerable set of IDs.
// - Tests can assert FUNNEL_STEP_IDS === question IDs ∪ virtual IDs so the
//   lists cannot drift silently when someone adds a question.

import { ALL_QUESTIONS } from './questions';

/** Virtual (non-question) screens in the funnel. */
export const VIRTUAL_STEP_IDS = Object.freeze([
    'welcome_0',
    'welcome_1',
    'welcome_2',
    'break_threshold_met',
    'below_threshold_end',
    'consent',
]);

/** Phase labels used on step_* events (non-PII metadata only). */
export const FUNNEL_PHASES = Object.freeze({
    WELCOME: 'welcome',
    ASRS: 'asrs',
    BREAK: 'break',
    IMPAIRMENT: 'impairment',
    PERSONAL: 'personal',
    PREVIOUS_ASSESSMENT: 'previousAssessment',
    COMPLETION: 'completion',
});

/**
 * Full allowlist of stepIds the client may emit.
 * Order: welcome → questions (as defined in ALL_QUESTIONS) → remaining virtuals.
 */
export const FUNNEL_STEP_IDS = Object.freeze([
    'welcome_0',
    'welcome_1',
    'welcome_2',
    ...ALL_QUESTIONS.map((q) => q.id),
    'break_threshold_met',
    'below_threshold_end',
    'consent',
]);

/**
 * Build the non-PII metadata payload for a question screen.
 * @param {{ id: string, phase: string }} question
 * @param {number} index - Index in ALL_QUESTIONS
 */
export function getQuestionStepMeta(question, index) {
    if (!question) return null;
    return {
        stepId: question.id,
        phase: question.phase,
        stepIndex: index,
    };
}

/**
 * Metadata for a welcome carousel slide (0–2).
 * @param {number} welcomeStep
 */
export function getWelcomeStepMeta(welcomeStep) {
    return {
        stepId: `welcome_${welcomeStep}`,
        phase: FUNNEL_PHASES.WELCOME,
        stepIndex: welcomeStep,
    };
}

/**
 * Whether an answer value carries nothing at all, across every shape the form
 * stores (plain values, name / email pairs, phone, address).
 * @param {*} value
 */
export function isAnswerEmpty(value) {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string') return value.trim() === '';
    if (typeof value === 'object') return Object.values(value).every(isAnswerEmpty);
    return false;
}

/**
 * Whether advancing past this screen counts as a SKIP for funnel reporting:
 * an optional question the person moved past without entering anything.
 *
 * A skip and an abandonment are different things and cannot be told apart after
 * the fact, so the distinction is recorded at the moment it happens. Required
 * questions are never skips — validation stops them being advanced empty — and
 * keeping that explicit means a future required question with a looser
 * validation rule still cannot be reported as one.
 *
 * @param {{ required?: boolean }} question
 * @param {*} answerValue - The value extracted from the form for this screen.
 */
export function isStepSkipped(question, answerValue) {
    if (!question || question.required) return false;
    return isAnswerEmpty(answerValue);
}
